import { Box, Divider, Grid, Text } from 'theme-ui';
import React, { useEffect } from 'react';
import { Stack } from '@bedrock-layout/stack';
import { Inline } from '@bedrock-layout/inline';
import { Split } from '@bedrock-layout/split';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import SiteNavigation from 'Components/molecules/SiteNavigation';
import BreadCrumbs from 'Components/molecules/Breadcrumbs';
import FormControls from './FormControls';
import Section from 'Components/molecules/Section';
import FieldGroup from 'Components/molecules/FieldGroup';
import InputField from 'Components/molecules/InputField';
import DropZone from 'Components/molecules/DropZone';
import Wysiwig from 'Components/molecules/Wysiwig';
import BrandingRadioGroup from 'Components/molecules/BrandingRadioGroup';
import SwitchField from 'Components/molecules/SwitchField';
import GoToTop from 'Components/molecules/GoToTop';
import { TENANT_FORM_FIELDS } from 'Constants/tenantFormFields';
import useTenant from 'Hooks/useTenant';
import { useParams } from 'react-router';
import { useFormContext } from 'react-hook-form';
import Loader from 'Components/molecules/Loader';
import Tooltip from 'Components/molecules/Tooltip';
import RadioField from 'Components/molecules/RadioField';
import SelectField from 'Components/molecules/SelectField';
import KeyValueFieldArray from 'Components/molecules/KeyValueFieldArray';
import { withFeatureFlagDefaults } from 'Constants/featureFlags';
import { codeFlagsToFieldArray } from 'Utils/codeFlags';

function TenantEdit() {
  const {
    reset,
    formState: { isSubmitting },
  } = useFormContext();
  const params = useParams();
  const { isLoading, data } = useTenant(params);

  useEffect(() => {
    if (data) {
      reset({ ...withFeatureFlagDefaults(data), codeFlags: codeFlagsToFieldArray(data.codeFlags) });
    }
    return reset();
  }, [data, params.id]);

  const { SYSTEM, HOMEPAGE_CONFIG, PAGE_CONTENT, READER_CONFIG, FEATURES, FUNDS, LEGAL, BANNER, CODE_FLAGS } =
    TENANT_FORM_FIELDS;

  if (isLoading) return <Loader isActive={true} />;

  return (
    <>
      <Loader isActive={isSubmitting} />
      <Grid variant="layout" bg="muted">
        <Grid variant="layout.header" bg="background" gap={0}>
          <SiteNavigation />
          <Divider variant="layout.header.divider" color="lightgray" />
          <BreadCrumbs />
          <FormControls />
        </Grid>
        <Box as="form" variant="layout.body" bg="background" pt="4" pb="6" px="5">
          <Stack gutter="lgXl">
            <GoToTop />
            <Section>
              <Text variant="section">
                {SYSTEM.TITLE}
                <Tooltip />
              </Text>
              <Split gutter="md" fraction="2/3">
                <FieldGroup>
                  {SYSTEM.FIELDS.map((field) => (
                    <InputField key={field.name} {...field} />
                  ))}
                </FieldGroup>
              </Split>
            </Section>
            <Divider color="divider" />
            <Section>
              <Text variant="section">
                {HOMEPAGE_CONFIG.TITLE}
                <Tooltip />
              </Text>
              <Split gutter="md" fraction="2/3">
                <FieldGroup>
                  {HOMEPAGE_CONFIG.FIELDS.map((field) => {
                    switch (field.component) {
                      case 'dropZone':
                        return <DropZone key={field.name} {...field} />;
                      case 'brandingField':
                        return <BrandingRadioGroup key={field.name} {...field} />;
                      case 'inputField':
                        return <InputField key={field.name} {...field} />;
                      case 'selectField':
                        return <SelectField key={field.name} {...field} />;
                      default:
                        return null;
                    }
                  })}
                </FieldGroup>
              </Split>
            </Section>
            <Divider color="divider" />
            <Section>
              <Text variant="section">
                {PAGE_CONTENT.TITLE}
                <Tooltip />
              </Text>
              <FieldGroup>
                {PAGE_CONTENT.FIELDS.map((field) => (
                  <Wysiwig key={field.name} {...field} />
                ))}
              </FieldGroup>
            </Section>
            <Divider color="divider" />
            <Section>
              <Text variant="section">
                {READER_CONFIG.TITLE}
                <Tooltip />
              </Text>
              <Split gutter="md" fraction="2/3">
                <Stack gutter="lg">
                  {READER_CONFIG.FIELDS.map((field) => {
                    switch (field.component) {
                      case 'switchField':
                        return (
                          <Inline key={field.name} gutter="md" stretch="start">
                            <SwitchField {...field} />
                          </Inline>
                        );
                      default:
                        return <InputField key={field.name} {...field} />;
                    }
                  })}
                </Stack>
              </Split>
            </Section>
            <Divider color="divider" />
            <Section>
              <Text variant="section">
                {FEATURES.TITLE}
                <Tooltip />
              </Text>
              <Split gutter="md" fraction="2/3">
                <FieldGroup>
                  <Stack gutter="xl">
                    {FEATURES.FIELDS.map((field) => (
                      <Inline key={field.name} gutter="md" stretch="start">
                        <SwitchField {...field} />
                      </Inline>
                    ))}
                  </Stack>
                </FieldGroup>
              </Split>
            </Section>
            <Divider color="divider" />
            <Section>
              <Text variant="section">
                {FUNDS.TITLE}
                <Tooltip />
              </Text>
              {FUNDS.FIELDS.map((field, index) => (
                <RadioField key={field.name + index} {...field} />
              ))}
            </Section>
            <Divider color="divider" />
            <Section>
              <Text variant="section">
                {LEGAL.TITLE}
                <Tooltip />
              </Text>
              <FieldGroup>
                {LEGAL.FIELDS.map((field) => {
                  switch (field.component) {
                    case 'wysiwigField':
                      return <Wysiwig key={field.name} {...field} />;
                    case 'inputField':
                      return <InputField key={field.name} {...field} />;
                    default:
                      return null;
                  }
                })}
              </FieldGroup>
            </Section>
            <Divider color="divider" />
            <Section>
              <Text variant="section">
                {BANNER.TITLE}
                <Tooltip />
              </Text>
              <FieldGroup>
                {BANNER.FIELDS.map((field) => {
                  switch (field.component) {
                    case 'switchField':
                      return (
                        <Inline key={field.name} gutter="md" stretch="start">
                          <SwitchField {...field} />
                        </Inline>
                      );
                    case 'inputField':
                      return <InputField key={field.name} {...field} />;
                    default:
                      return null;
                  }
                })}
              </FieldGroup>
            </Section>
            <Divider color="divider" />
            <Section>
              <Text variant="section">
                {CODE_FLAGS.TITLE}
                <Tooltip text={CODE_FLAGS.TOOLTIP_TEXT} />
              </Text>
              <FieldGroup>
                <KeyValueFieldArray name="codeFlags" />
              </FieldGroup>
            </Section>
          </Stack>
        </Box>
      </Grid>
    </>
  );
}
export default TenantEdit;
